import { useState, useEffect, useRef, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider, useCart } from './contexts/CartContext';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import ActionButtons from './components/ActionButtons';
import Banner from './components/Banner';
import Categories from './components/Categories';
import ProductCard from './components/ProductCard';
import QuickSearch from './components/QuickSearch';
import CartBadge from './components/CartBadge';
import CartPanel from './components/CartPanel';
import LoginModal from './components/LoginModal';
import Footer from './components/Footer';
import MyOrders from './pages/MyOrders';
import PanelDashboard from './pages/PanelDashboard';
import { api } from './services/api';
import type { Product, Category } from './types/api';

const PAGE_SIZE = 100;

function normalizeHexColor(value: string, fallback: string): string {
  const color = value.trim();
  const short = /^#([0-9a-fA-F]{3})$/;
  const long = /^#([0-9a-fA-F]{6})$/;

  if (long.test(color)) return color;
  if (short.test(color)) {
    const hex = color.slice(1);
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  return fallback;
}

function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  const parsed = Number.parseInt(value, 16);
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

function mixWithWhite(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(r)} ${mix(g)} ${mix(b)})`;
}

function mixWithBlack(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (channel: number) => Math.round(channel * (1 - amount));
  return `rgb(${mix(r)} ${mix(g)} ${mix(b)})`;
}

function getPageFromPath(pathname: string): 'catalog' | 'orders' | 'settings' {
  if (pathname === '/painel' || pathname === '/admin') return 'settings';
  if (pathname === '/pedidos') return 'orders';
  return 'catalog';
}

function AppContent() {
  const { isAuthenticated, user, logout } = useAuth();
  const { addItem, totalItems } = useCart();

  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productError, setProductError] = useState('');
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [currentPage, setCurrentPage] = useState<'catalog' | 'orders' | 'settings'>(() => getPageFromPath(window.location.pathname));
  const [storeConfig, setStoreConfig] = useState<{ name: string; logo: string; phone: string; commercialMessage: string; primaryColor: string; priceRequiresLogin: boolean } | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const productsSectionRef = useRef<HTMLElement | null>(null);

  function navigate(path: '/painel' | '/pedidos' | '/') {
    window.history.pushState({}, '', path);
    setCurrentPage(getPageFromPath(path));
    setShowQuickSearch(false);
  }

  const fetchProducts = useCallback(async (opts: {
    cat?: Category | null;
    pg?: number;
    q?: string;
    append?: boolean;
  }) => {
    const { cat, pg = 1, q, append } = opts;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!append) {
      setLoadingProducts(true);
    } else {
      setLoadingMore(true);
    }
    setProductError('');

    try {
      const params: Record<string, unknown> = { limit: PAGE_SIZE, page: pg };
      if (cat?.categoryId != null) params.categoryId = cat.categoryId;
      if (q) params.q = q;

      const fetcher = isAuthenticated ? api.getProducts.bind(api) : api.getPublicProducts.bind(api);
      const res = await fetcher(params as any);

      if (controller.signal.aborted) return;

      if (append) {
        setProducts(prev => [...prev, ...res.data]);
      } else {
        setProducts(res.data);
      }
      setTotal(res.total);
      setPage(res.page);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setProductError(err instanceof Error ? err.message : 'Erro ao buscar');
    } finally {
      setLoadingProducts(false);
      setLoadingMore(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handlePopState = () => setCurrentPage(getPageFromPath(window.location.pathname));
    window.addEventListener('popstate', handlePopState);

    api.getStoreConfig()
      .then(setStoreConfig)
      .catch(() => {});

    api.getCategories()
      .then(setCategories)
      .catch(() => {});

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (currentPage === 'settings') {
      document.title = 'Villamix Express | Painel';
      return;
    }

    if (currentPage === 'orders') {
      document.title = 'Villamix Express | Pedidos';
      return;
    }

    document.title = 'Villamix Express';
  }, [currentPage]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const primaryColor = normalizeHexColor(storeConfig?.primaryColor || '#38c738', '#38c738');
    const root = document.documentElement;

    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--color-primary-dark', mixWithBlack(primaryColor, 0.14));
    root.style.setProperty('--color-primary-light', mixWithWhite(primaryColor, 0.9));
  }, [storeConfig?.primaryColor]);

  useEffect(() => {
    setPage(1);
    setProducts([]);
    fetchProducts({ cat: selectedCategory, pg: 1, q: searchQuery });
  }, [selectedCategory, searchQuery, isAuthenticated, fetchProducts]);

  function handleCategorySelect(category: Category | null) {
    setSelectedCategory(category);
  }

  function handleSearch(query: string) {
    setSearchQuery(query);
    setSelectedCategory(null);
  }

  async function handleQuickSearch(query: string): Promise<Product[]> {
    try {
      const fetcher = isAuthenticated ? api.getProducts.bind(api) : api.getPublicProducts.bind(api);
      const res = await fetcher({ q: query, limit: 20 } as any);
      return res.data.filter(product => product.available);
    } catch {
      return [];
    }
  }

  function handleLoadMore() {
    fetchProducts({ cat: selectedCategory, pg: page + 1, q: searchQuery, append: true });
  }

  function handleAddToCart(productId: string, productName: string, price: number, quantity = 1, weighable = false) {
    addItem(productId, productName, price, quantity, weighable);
  }

  const visibleProducts = products.filter(product => product.available);

  const catalogAwaitingSync = !loadingProducts && total === 0 && products.length === 0 && !searchQuery && selectedCategory === null;

  if (currentPage === 'orders') {
    return (
      <div className="app">
        <Header
          user={user}
          logoSrc={storeConfig?.logo}
          onLogout={logout}
          onOrders={() => navigate('/pedidos')}
          onBackToCatalog={() => navigate('/')}
          showBack
        />
        <MyOrders />
        <Footer />
      </div>
    );
  }

  if (currentPage === 'settings') {
    return (
      <div className="app">
        <Header
          user={user}
          logoSrc={storeConfig?.logo}
          onBackToCatalog={() => navigate('/')}
          showBack
        />
        <main className="main">
          <PanelDashboard
            storeConfig={storeConfig}
            onStoreSaved={cfg => setStoreConfig(cfg)}
            onBackToCatalog={() => navigate('/')}
            onLogout={() => {
              logout();
              navigate('/');
            }}
          />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        user={user}
        logoSrc={storeConfig?.logo}
        onLogout={logout}
        onOrders={() => navigate('/pedidos')}
      />

      <main className="main">
        {showQuickSearch ? (
          <QuickSearch
            onBack={() => setShowQuickSearch(false)}
            isAuthenticated={isAuthenticated}
            onSearch={handleQuickSearch}
            onAddToCart={handleAddToCart}
          />
        ) : (
          <>
            <SearchBar onSearch={handleSearch} />
            <ActionButtons
              onQuickSearch={() => setShowQuickSearch(true)}
              onLogin={() => setShowLogin(true)}
              isAuthenticated={isAuthenticated}
              userName={user?.name}
            />
            <Banner />
            <Categories
              categories={categories}
              selectedId={selectedCategory?.id ?? null}
              onSelect={handleCategorySelect}
            />
            <section className="products-section" ref={productsSectionRef}>
              <div className="products-header">
                <h2>Destaques do mercado {total > 0 && `(${total})`}</h2>
                {isAuthenticated && <span className="products-auth-badge">Catálogo ao vivo</span>}
              </div>

              {loadingProducts ? (
                <div className="products-loading">Carregando...</div>
              ) : productError ? (
                <div className="products-error">{productError}</div>
              ) : visibleProducts.length === 0 ? (
                <div className="products-empty">
                  {catalogAwaitingSync ? 'Catálogo aguardando atualização.' : 'Nenhuma oferta encontrada.'}
                </div>
              ) : (
                <>
                  <div className="products-grid">
                    {visibleProducts.map(p => (
                      <ProductCard
                        key={p.id}
                        product={p}
                        isAuthenticated={isAuthenticated}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </div>
                  {products.length < total && (
                    <div className="products-loadmore">
                      <button onClick={handleLoadMore} disabled={loadingMore}>
                        {loadingMore ? 'Carregando...' : `Ver mais ofertas (${products.length}/${total})`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </main>

      <CartBadge count={totalItems} onClick={() => setShowCart(true)} />
      <CartPanel open={showCart} onClose={() => setShowCart(false)} onFinalizeGuest={() => setShowLogin(true)} />
      <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />

      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </AuthProvider>
  );
}
